
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getAddresses } from "../../../services/addressService";
import { submitPharmacyOrder } from "../../../services/pharmacy/pharmacyOrderService";

import AddressModal from "../../../components/AddressModal";

import SelectedAddressCard from "../../../components/Checkout/Step2Address/components/SelectedAddressCard/SelectedAddressCard";
import AddressList from "../../../components/Checkout/Step2Address/components/AddressList/AddressList";
import OrderComment from "../../../components/Checkout/Step2Address/components/OrderComment/OrderComment";

import { usePrescriptionCart } from "../../../context/pharmacy/PrescriptionCartContext";

import PrescriptionOrderStepper from "./components/PrescriptionOrderStepper";

import {
    Image as ImageIcon,
    Pill,
    ShieldCheck,
    UserRound,
    Hash,
    Package,
    FileText,
    Trash2,
} from "lucide-react";

import "./prescription-orders.css";
import "./PrescriptionCartStep1.css";

export default function PrescriptionCartStep1({
    items = [],
    onAddressSelected,
    onContinue,
    onBack,
}) {
    const navigate = useNavigate();

    const {
        removeItem,
        clearCart,
    } = usePrescriptionCart();

    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [comment, setComment] = useState("");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [showCreateAddress, setShowCreateAddress] = useState(false);
    const [highlightList, setHighlightList] = useState(false);

    const addressListRef = useRef(null);

    const [form, setForm] = useState({
        name: "",
        lname: "",
        mobile: "",
        postalCode: "",
        address: "",
        provinceId: "",
        cityId: "",
        status: 1,
    });

    // ----------------------------------------
    // Load addresses
    // ----------------------------------------

    useEffect(() => {
        let mounted = true;

        const loadAddresses = async () => {
            try {
                setLoading(true);
                setError("");

                const data = await getAddresses();

                if (!mounted) return;

                const list = Array.isArray(data) ? data : [];

                setAddresses(list);

                const defaultAddress =
                    list.find(
                        (address) => Number(address.status) === 1
                    ) ||
                    list[0] ||
                    null;

                setSelectedAddress(defaultAddress);

                if (defaultAddress) {
                    onAddressSelected?.(defaultAddress);
                }

            } catch (err) {
                console.error("Failed to load addresses:", err);

                if (mounted) {
                    setError("امکان دریافت آدرس‌ها وجود ندارد.");
                }

            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadAddresses();

        return () => {
            mounted = false;
        };
    }, []);

    // ----------------------------------------
    // Select address
    // ----------------------------------------

    const handleSelectAddress = (address) => {
        setSelectedAddress(address);
        onAddressSelected?.(address);
    };

    const handleChangeAddress = () => {
        addressListRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });

        setHighlightList(true);

        window.setTimeout(() => {
            setHighlightList(false);
        }, 1500);
    };

    // ----------------------------------------
    // Refresh addresses after creating address
    // ----------------------------------------

    const refreshAddresses = async (preferredAddress = null) => {
        try {
            const data = await getAddresses();

            const list = Array.isArray(data) ? data : [];

            setAddresses(list);

            let addressToSelect = null;

            if (preferredAddress?.id) {
                addressToSelect =
                    list.find(
                        (address) =>
                            Number(address.id) ===
                            Number(preferredAddress.id)
                    ) || preferredAddress;
            }

            if (!addressToSelect && list.length > 0) {
                addressToSelect = list[0];
            }

            if (addressToSelect) {
                handleSelectAddress(addressToSelect);
            }

            return addressToSelect;

        } catch (err) {
            console.error("Failed to refresh addresses:", err);
            return null;
        }
    };

    const handleAddressCreated = async (response) => {
        const newAddress =
            response?.address ||
            response?.addresses ||
            response?.data?.address ||
            response?.data ||
            response;

        setShowCreateAddress(false);

        await refreshAddresses(newAddress);
    };

    const handleAddressSaved = async () => {
        setShowCreateAddress(false);

        await refreshAddresses();
    };

    // ----------------------------------------
    // Remove item
    // ----------------------------------------

    const handleRemoveItem = (index) => {
        removeItem(index);
    };

    // ----------------------------------------
    // Convert frontend items to Laravel payload
    // ----------------------------------------

    const prepareOrderItems = () => {
        return items.map((item) => {

            // Image prescription
            if (item.type === "imagePrescription") {
                return {
                    type: "imagePrescription",
                    image: item.image,
                };
            }

            // Non-prescription / OTC medicine
            if (item.type === "otc") {
                return {
                    type: "otc",

                    drugName: item.drugName,
                    drugDose: item.drugDose || null,

                    qty: Number(item.qty || 1),

                    drugType: item.drugType
                        ? Number(item.drugType)
                        : null,

                    unit: item.unit
                        ? Number(item.unit)
                        : null,

                    sameDrug: Boolean(item.sameDrug),

                    image: item.image || null,
                };
            }

            // Electronic insurance prescription
            if (item.type === "insurancePrescription") {

                let insuranceType = Number(item.insuranceType);

                // If insuranceType is a Persian name instead of a number,
                // convert it to the database values.
                if (Number.isNaN(insuranceType)) {
                    const insuranceName = String(
                        item.insuranceType || ""
                    );

                    insuranceType = insuranceName.includes("تامین")
                        ? 0
                        : 1;
                }

                return {
                    type: "insurancePrescription",

                    insuranceType,

                    nationalCode: item.nationalCode,
                    doctorName: item.doctorName,
                    trackingCode: item.trackingCode || "",
                };
            }

            return null;
        }).filter(Boolean);
    };

    // ----------------------------------------
    // Submit order to Laravel
    // ----------------------------------------

    const handleContinue = async () => {

        if (submitting) return;

        if (!selectedAddress?.id) {
            alert("لطفاً آدرس تحویل را انتخاب کنید.");
            return;
        }

        if (!items || items.length === 0) {
            alert("سبد سفارش شما خالی است.");
            return;
        }

        try {
            setSubmitting(true);
            setError("");

            const payload = {
                address_id: Number(selectedAddress.id),

                comment: comment || "",

                items: prepareOrderItems(),
            };

            console.log("Pharmacy order payload:", payload);

            // This is where your Laravel API is called.
            const result = await submitPharmacyOrder(payload);

            console.log("Saved pharmacy order:", result);

            const orderId = result?.order?.id;

            if (!orderId) {
                throw new Error(
                    "Order was saved but the order ID was not returned."
                );
            }

            // Clear local prescription cart after successful API response.
            clearCart();

            // Go to Step 2.
            navigate(`/dashboard/pharmacy/orders/${orderId}`);

        } catch (err) {
            console.error("Submit pharmacy order failed:", err);

            const validationErrors =
                err.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(validationErrors)[0];

                setError(
                    Array.isArray(firstError)
                        ? firstError[0]
                        : "اطلاعات سفارش معتبر نیست."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "ثبت سفارش با خطا مواجه شد."
                );
            }

        } finally {
            setSubmitting(false);
        }
    };

    // ----------------------------------------
    // Order titles
    // ----------------------------------------

    const getOrderTitle = (item) => {
        switch (item.type) {
            case "imagePrescription":
                return "سفارش دارو با عکس نسخه";

            case "otc":
                return "سفارش داروی بدون نسخه";

            case "insurancePrescription":
                return `سفارش دارو با نسخه بیمه ${item.insuranceType || ""}`;

            default:
                return "سفارش دارو";
        }
    };

    // ----------------------------------------
    // Render order items
    // ----------------------------------------

    const renderOrderItem = (item, index) => {

        switch (item.type) {

            case "imagePrescription":
                return (
                    <div
                        className="prescription-order-card"
                        key={item.id || index}
                    >
                        <div className="prescription-order-heading">

                            <div className="prescription-order">
                                <div className="prescription-order-icon image-icon">
                                    <ImageIcon size={21} />
                                </div>

                                <div className="prescription-order-item">
                                    <h3>{getOrderTitle(item)}</h3>
                                    <p>تصویر نسخه بارگذاری‌شده</p>
                                </div>
                            </div>

                            <div className="prescription-remove">
                                <button
                                    type="button"
                                    className="prescription-remove-item"
                                    onClick={() => handleRemoveItem(index)}
                                    title="حذف سفارش"
                                    aria-label="حذف سفارش"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>

                        {item.image && (
                            <div className="prescription-image-preview">
                                <img
                                    src={item.image}
                                    alt="تصویر نسخه"
                                />
                            </div>
                        )}

                        {item.fileName && (
                            <div className="prescription-file-name">
                                <FileText size={16} />
                                <span>{item.fileName}</span>
                            </div>
                        )}
                    </div>
                );

            case "otc":
                return (
                    <div
                        className="prescription-order-card"
                        key={item.id || index}
                    >
                        <div className="prescription-order-heading">

                            <div className="prescription-order">
                                <div className="prescription-order-icon otc-icon">
                                    <Pill size={21} />
                                </div>

                                <div className="prescription-order-item">
                                    <h3>{getOrderTitle(item)}</h3>
                                    <p>
                                        {item.drugName || "نام دارو ثبت نشده است"}
                                    </p>
                                </div>
                            </div>

                            <div className="prescription-remove">
                                <button
                                    type="button"
                                    className="prescription-remove-item"
                                    onClick={() => handleRemoveItem(index)}
                                    title="حذف سفارش"
                                    aria-label="حذف سفارش"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="prescription-order-details">

                            <div>
                                <span>تعداد</span>
                                <strong>{item.qty ?? "-"}</strong>
                            </div>

                            <div>
                                <span>نوع محصول</span>
                                <strong>{item.drugTypeName || "-"}</strong>
                            </div>

                            <div>
                                <span>واحد محصول</span>
                                <strong>{item.unitName || "-"}</strong>
                            </div>

                            {item.drugDose && (
                                <div>
                                    <span>دوز دارو</span>
                                    <strong>{item.drugDose}</strong>
                                </div>
                            )}

                        </div>

                        {item.image && (
                            <div className="prescription-image-preview small-preview">
                                <img
                                    src={item.image}
                                    alt={item.drugName || "تصویر دارو"}
                                />
                            </div>
                        )}
                    </div>
                );

            case "insurancePrescription":
                return (
                    <div
                        className="prescription-order-card"
                        key={item.id || index}
                    >
                        <div className="prescription-order-heading">

                            <div className="prescription-order">
                                <div className="prescription-order-icon insurance-icon">
                                    <ShieldCheck size={21} />
                                </div>

                                <div className="prescription-order-item">
                                    <h3>{getOrderTitle(item)}</h3>
                                    <p>اطلاعات نسخه بیمه</p>
                                </div>
                            </div>

                            <div className="prescription-remove">
                                <button
                                    type="button"
                                    className="prescription-remove-item"
                                    onClick={() => handleRemoveItem(index)}
                                    title="حذف سفارش"
                                    aria-label="حذف سفارش"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="prescription-order-details insurance-details">

                            <div>
                                <span>
                                    <Hash size={14} />
                                    کد ملی
                                </span>

                                <strong dir="ltr">
                                    {item.nationalCode || "-"}
                                </strong>
                            </div>

                            <div>
                                <span>
                                    <UserRound size={14} />
                                    نام پزشک
                                </span>

                                <strong>
                                    {item.doctorName || "-"}
                                </strong>
                            </div>

                            {item.trackingCode && (
                                <div>
                                    <span>کد رهگیری</span>
                                    <strong>{item.trackingCode}</strong>
                                </div>
                            )}

                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // ----------------------------------------
    // Loading addresses
    // ----------------------------------------

    if (loading) {
        return (
            <section className="prescription-step-one" dir="rtl">
                <div className="prescription-step-loading">
                    <span className="prescription-spinner" />
                    <p>در حال دریافت آدرس‌ها...</p>
                </div>
            </section>
        );
    }

    if (error && !addresses.length) {
        return (
            <section className="prescription-step-one" dir="rtl">
                <div className="prescription-step-error">
                    <p>{error}</p>

                    <button type="button" onClick={onBack}>
                        بازگشت
                    </button>
                </div>
            </section>
        );
    }

    // ----------------------------------------
    // Main UI
    // ----------------------------------------

    return (
        <section className="prescription-step-one" dir="rtl">

            <PrescriptionOrderStepper status="submitted" />

            <div className="prescription-step-layout">

                <div className="prescription-step-main">

                    <div className="prescription-section-title">
                        <h2>سفارش‌های شما</h2>
                        <span>{items.length} مورد</span>
                    </div>

                    {items.length === 0 ? (
                        <div className="prescription-empty">
                            <Package size={35} />
                            <p>
                                هیچ سفارشی در سبد نسخه شما وجود ندارد.
                            </p>
                        </div>
                    ) : (
                        <div className="prescription-order-list">
                            {items.map((item, index) =>
                                renderOrderItem(item, index)
                            )}
                        </div>
                    )}

                    {/* Address section */}

                    <div className="prescription-address-section">

                        <div className="prescription-section-title">
                            <h2>انتخاب آدرس تحویل</h2>
                        </div>

                        <div className="prescription-selected-address">
                            <SelectedAddressCard
                                address={selectedAddress}
                                onChange={handleChangeAddress}
                                onCreate={() => setShowCreateAddress(true)}
                            />
                        </div>

                        <div
                            ref={addressListRef}
                            className={`prescription-address-list ${
                                highlightList ? "highlight-panel" : ""
                            }`}
                        >
                            <AddressList
                                addresses={addresses}
                                selectedAddress={selectedAddress}
                                onConfirm={handleSelectAddress}
                            />
                        </div>

                    </div>

                    {/* Order comment */}

                    <div className="prescription-comment-section">
                        <OrderComment
                            value={comment}
                            onChange={setComment}
                        />
                    </div>

                    {/* API error */}

                    {error && (
                        <div className="prescription-step-error">
                            <p>{error}</p>
                        </div>
                    )}

                </div>
            </div>

            {/* Footer buttons */}

            <div className="prescription-step-actions">

                <button
                    type="button"
                    className="prescription-back-btn"
                    onClick={onBack}
                    disabled={submitting}
                >
                  بازگشت به نسخه های من
                </button>

                <button
                    type="button"
                    className="prescription-continue-btn"
                    disabled={
                        !selectedAddress ||
                        items.length === 0 ||
                        submitting
                    }
                    onClick={handleContinue}
                >
                    {submitting ? (
                        <>
                            در حال ثبت سفارش...
                        </>
                    ) : (
                        <>
                            تکمیل سفارش
                            <span>←</span>
                        </>
                    )}
                </button>

            </div>

            {/* Create address modal */}

            {showCreateAddress && (
                <AddressModal
                    onClose={() => setShowCreateAddress(false)}
                    onSuccess={handleAddressCreated}
                    onSave={handleAddressSaved}
                    form={form}
                    setForm={setForm}
                />
            )}

        </section>
    );
}
